import type { ScopedAccessDTO } from "@/shared/types/admin/scopedAccess";
import { api, CustomAxiosRequestConfig } from "@/shared/api/client";
import { LARGE_PAGE_SIZE } from "@/shared/config/pagination";
import {
  UsersRequestDTO,
  PageUsersGetDTO,
  UserCreateDTO,
  UserUpdateDTO,
  UserDTO,
  UserImageDTO,
  PageRolesGetDTO,
  WalletUserFileGetDTO,
  WalletUserCreateDTO,
  WalletUserNumGetDTO,
  WalletUploadDTO,
  GenerateSpreadsheetParams,
  GeneratePdfParams,
  UserChatGetDTO,
  ProfessionDTO,
} from "@/shared/types/admin";
import { fileHelper } from "@/shared/lib/fileHelper";

import {
    WorkerTaskRequestDTO,
    PageWorkerTasksGetDTO,
} from "@/shared/types/project";

export const userService = {
  getAll: async (params: UsersRequestDTO): Promise<PageUsersGetDTO> => {
    const response = await api.get<PageUsersGetDTO>("/users", {
      params: {
        page: params.page - 1,
        size: params.size,
        name: params.name,
        email: params.email,
        role: params.role,
        active: params.active,
      },
    });
    return response.data;
  },

  getByUsername: async (username: string): Promise<UserDTO> => {
    const { data } = await api.get<UserDTO>(`/users/${username}`, {
      skipErrorToast: true
    });
    return data;
  },

  getUserTasks: async (
    username: string,
    params: WorkerTaskRequestDTO
  ): Promise<PageWorkerTasksGetDTO> => {
    const response = await api.get<PageWorkerTasksGetDTO>(
        `/users/${username}/tasks`,
        {
            params: {
                page: params.page - 1,
                size: params.size,
                sort: params.sort,
                ...params.filter,
            },
        }
    );
    return response.data;
  },

  create: async (data: UserCreateDTO): Promise<UserDTO> => {
    const response = await api.post<UserDTO>("/users", data);
    return response.data;
  },

  update: async (username: string, data: UserUpdateDTO): Promise<UserDTO> => {
    const response = await api.put<UserDTO>(`/users/${username}`, data);
    return response.data;
  },

  updateImage: async (username: string, file: File): Promise<void> => {
    const formData = fileHelper.toMultipartFormData(file, "file");
    await api.put(`/users/${username}/profile/img`, formData);
  },

  getRoles: async (): Promise<PageRolesGetDTO> => {
    const response = await api.get<PageRolesGetDTO>("/roles", {
      params: {
        page: 0,
        size: LARGE_PAGE_SIZE,
        sort: "name,asc",
      },
    });
    return response.data;
  },

  /** Profissões ativas: só sugerem acessos no cadastro. */
  getProfessions: async (): Promise<ProfessionDTO[]> => {
    const response = await api.get<ProfessionDTO[]>("/professions", {
      skipErrorToast: true,
    });
    return response.data;
  },

  /** Obras e armazéns da pessoa com os acessos de cada um. */
  getScopedAccess: async (username: string): Promise<ScopedAccessDTO> => {
    const response = await api.get<ScopedAccessDTO>(`/users/${encodeURIComponent(username)}/scoped-access`);
    return response.data;
  },

  getUserRoles: async (username: string): Promise<string[]> => {
    const response = await api.get<string[]>(`/users/${username}/roles`);
    return response.data;
  },

  disable: async (username: string) => {
    const response = await api.patch(`/users/${username}/disable`, {});
    return response.data;
  },

  enable: async (username: string) => {
    const response = await api.patch(`/users/${username}/enable`, {});
    return response.data;
  },

  /** Reenvia o convite (o link anterior deixa de valer); também convida de volta quem foi removido. */
  resendInvitation: async (username: string): Promise<void> => {
    await api.post(`/users/${username}/invitation`, {}, { skipErrorToast: true } as CustomAxiosRequestConfig);
  },

  cancelInvitation: async (username: string): Promise<void> => {
    await api.delete(`/users/${username}/invitation`, { skipErrorToast: true } as CustomAxiosRequestConfig);
  },

  /** Tira a pessoa da organização; ela só volta com um convite novo. A conta dela não muda. */
  removeMembership: async (username: string): Promise<void> => {
    await api.delete(`/users/${username}/membership`, { skipErrorToast: true } as CustomAxiosRequestConfig);
  },

  getWallet: async (username: string): Promise<WalletUserFileGetDTO> => {
    const response = await api.get<WalletUserFileGetDTO>(
      `/users/${username}/wallets`,
      { skipErrorToast: true } as CustomAxiosRequestConfig
    );
    return response.data;
  },

  createWallet: async (
    username: string,
    data: WalletUserCreateDTO
  ): Promise<WalletUserNumGetDTO> => {
    const response = await api.post<WalletUserNumGetDTO>(
      `/users/${username}/wallets`,
      data,
      { skipErrorToast: true } as CustomAxiosRequestConfig
    );
    return response.data;
  },

  deleteWallet: async (username: string): Promise<void> => {
    await api.delete(`/users/${username}/wallets`);
  },

  uploadWallet: async (
    username: string,
    data: WalletUploadDTO
  ): Promise<WalletUserFileGetDTO> => {
    const response = await api.post<WalletUserFileGetDTO>(
      `/users/${username}/wallets/upload`,
      data
    );
    return response.data;
  },

  uploadWalletWithFiles: async (
    username: string,
    dto: WalletUserCreateDTO,
    files: File[]
  ): Promise<WalletUserFileGetDTO> => {
    const formData = new FormData();
    formData.append("data", new Blob([JSON.stringify(dto)], { type: "application/json" }));
    for (const file of files) {
      formData.append("files", file);
    }
    const response = await api.post<WalletUserFileGetDTO>(
      `/users/${username}/wallets/upload`,
      formData
    );
    return response.data;
  },

  generateSpreadsheet: async (
    username: string,
    params: GenerateSpreadsheetParams
  ): Promise<WalletUserFileGetDTO> => {
    const response = await api.post<WalletUserFileGetDTO>(
      `/users/${username}/wallets/generate_spreadsheet`,
      null,
      {
        params: {
          headerColumns: params.headerColumns,
          rawData: params.rawData,
          ext: params.ext,
          sheetName: params.sheetName,
        },
      }
    );
    return response.data;
  },

  generatePdf: async (
    username: string,
    params: GeneratePdfParams
  ): Promise<WalletUserFileGetDTO> => {
    const response = await api.post<WalletUserFileGetDTO>(
      `/users/${username}/wallets/generate-pdf`,
      null,
      {
        params: {
          nameTemplate: params.nameTemplate,
          reportName: params.reportName,
        },
      }
    );
    return response.data;
  },

  addFile: async (
    username: string,
    file: File,
    filename?: string
  ): Promise<WalletUserFileGetDTO> => {
    const formData = new FormData();
    formData.append("file", file, filename || file.name);
    const response = await api.post<WalletUserFileGetDTO>(
      `/users/${username}/wallets/add-file`,
      formData,
      {
        skipErrorToast: true,
      }
    );
    return response.data;
  },

  addCredential: async (
    username: string,
    credentials: Record<string, string>
  ): Promise<WalletUserNumGetDTO> => {
    const response = await api.post<WalletUserNumGetDTO>(
      `/users/${username}/wallets/add-credential`,
      credentials,
      { skipErrorToast: true } as CustomAxiosRequestConfig
    );
    return response.data;
  },

  deleteFile: async (username: string, fileId: string): Promise<void> => {
    await api.delete(`/users/${username}/wallets/delete-file/${fileId}`);
  },

  getFile: async (username: string, fileId: string): Promise<Blob> => {
    const response = await api.get(
      `/users/${username}/wallets/${fileId}`,
      { responseType: "blob" }
    );
    return response.data;
  },

  deleteCredential: async (
    username: string,
    credentialKey: string
  ): Promise<void> => {
    await api.delete(
      `/users/${username}/wallets/delete-credential/${credentialKey}`
    );
  },

  getChatProfile: async (id: string): Promise<UserChatGetDTO> => {
    const { data } = await api.get<UserChatGetDTO>(`/users/${id}/chat-profile`);
    return data;
  },
};

